import React, { useEffect, useState, useContext } from "react";
import {
    assistantsList,
    getUserId,
    submitAssistantFile,
    submitEditAssistant,
    deleteAssistantFiles,
    enableAssistant,
    disableAssistant,
    resolveEntities
} from "../../../api/api";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../../authConfig";
import { AppStateContext } from "../../../state/AppProvider";
import { UserSearchBox } from "../ui/UserSearchBox";
import { SearchEntity } from "../../../api/models";
import ClipLoader from "react-spinners/ClipLoader";
import { ErrorDialog } from "../../ErrorDialog";
import isEqual from "lodash/isEqual";
import { TitleField } from "../ui/TitleField";
import { PromptField } from "../ui/PromptField";
import { ExamplesField } from "../ui/ExamplesField";
import { FileUploadField } from "../ui/FileUploadField";
import { useKnowledgeUpload } from "../../../hooks/useKnowledgeUpload";
import { useFileProcessing } from "../../../hooks/useFileProcessing";
import { FileProcessingModal } from "../ui/FileProcessingModal";
import { ContactsField } from "../ui/ContactsField";
import "../ui/AssistantBuilder.css";

interface AssistantEditorProps {
    id: string;
    onSubmit: () => void;
    onCancel: () => void;
    isAdminMode?: boolean;
    isFileManagerMode?: boolean;
}

export const AssistantEditor = ({ id, onSubmit, onCancel, isAdminMode, isFileManagerMode }: AssistantEditorProps) => {
    const { instance: client } = useMsal();
    const appStateContext = useContext(AppStateContext);

    const knowledge_limit = 500;
    const knowledge_size = 500; // mb

    // existing data
    const [assistantFiles, setAssistantFiles] = useState<File[]>([]);
    const [oldTitle, setOldTitle] = useState("");
    const [oldPrompt, setOldPrompt] = useState("");
    const [oldExamples, setOldExamples] = useState<string[]>(["", "", ""]);
    const [oldContact, setOldContact] = useState<{ name: string; email: string }>({ name: "", email: "" });
    const [existingUserEntities, setExistingUserEntities] = useState<SearchEntity[]>([]);
    const [existingAdminEntities, setExistingAdminEntities] = useState<SearchEntity[]>([]);
    const [existingFileManagerEntities, setExistingFileManagerEntities] = useState<SearchEntity[]>([]);

    // editable fields
    const [title, setTitle] = useState("");
    const [prompt, setPrompt] = useState("");
    const [examples, setExamples] = useState<string[]>(["", "", ""]);
    const [contact, setContact] = useState<{ name: string; email: string }>({ name: "", email: "" });
    const [userEntities, setUserEntities] = useState<SearchEntity[]>([]);
    const [adminEntities, setAdminEntities] = useState<SearchEntity[]>([]);
    const [fileManagerEntities, setFileManagerEntities] = useState<SearchEntity[]>([]);

    // upload/delete bookkeeping
    const [deletedFiles, setDeletedFiles] = useState<File[]>([]);
    const {
        files: uploadedFiles,
        addFiles: addNewFiles,
        removeFile: removeNewFile
    } = useKnowledgeUpload(knowledge_limit, knowledge_size, msg => openErrorDialog(msg), assistantFiles.length);

    // ui state
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const [currentPhase, setCurrentPhase] = useState<string>("");
    const [err, setErr] = useState("");
    const [showErr, setShowErr] = useState(false);

    const { currentFileProgress, currentFileIndex, totalFiles, uploadDocument, resetProgress } = useFileProcessing();

    const openErrorDialog = (msg: string) => {
        setErr(msg);
        setShowErr(true);
    };

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                onSubmit();
                onCancel();
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, [success, onSubmit, onCancel]);

    // load assistant data
    useEffect(() => {
        const load = async () => {
            const all = appStateContext?.state.assistants || []; //all assistants

            const assistants = all.find(a => a.id === id);
            if (!assistants) return;

            //setting existing data
            setOldTitle(assistants.title);
            setTitle(assistants.title);
            setOldPrompt(assistants.system_prompt_message);
            setPrompt(assistants.system_prompt_message);
            setAssistantFiles(assistants.files || []);

            const examples = assistants.examples?.slice(0, 3) || [];
            while (examples.length < 3) examples.push("");
            setExamples(examples);
            setOldExamples(examples);

            const contact = {
                name: assistants.contact?.name || "",
                email: assistants.contact?.email || ""
            };
            setContact(contact);
            setOldContact(contact);

            const userIds = assistants.userIds ?? [];
            const groupIds = assistants.groups ?? [];
            const adminIds = assistants.adminIds ?? [];
            const adminGroups = assistants.adminGroups ?? [];
            const fileManagerIds = assistants.fileManagerIds ?? [];
            const fileManagerGroups = assistants.fileManagerGroups ?? [];

            if (!userIds.length && !groupIds.length && !adminIds.length && !adminGroups.length && !fileManagerIds.length && !fileManagerGroups.length) return;

            try {
                const token = await getToken(client);
                const userEntities = await resolveEntities(userIds, groupIds, token?.accessToken);
                setExistingUserEntities(userEntities);
                setUserEntities(userEntities);

                const adminEntities = await resolveEntities(adminIds, adminGroups, token?.accessToken);
                setExistingAdminEntities(adminEntities);
                setAdminEntities(adminEntities);

                const fileManagerEntities = await resolveEntities(fileManagerIds, fileManagerGroups, token?.accessToken);
                setExistingFileManagerEntities(fileManagerEntities);
                setFileManagerEntities(fileManagerEntities);

                console.log("Resolved user entities:", userEntities);
                console.log("Resolved admin entities:", adminEntities);
                console.log("Resolved file manager entities:", fileManagerEntities);
            } catch (e) {
                console.error("resolveEntities failed", e);
            }
        };
        load();
    }, [id]);

    // helpers
    const combinedFiles = [...assistantFiles, ...uploadedFiles];

    const removeCombined = (idx: number) => {
        if (idx < assistantFiles.length) {
            const target = assistantFiles[idx];
            setAssistantFiles(p => p.filter((_, i) => i !== idx));
            setDeletedFiles(p => [...p, target]);
        } else {
            removeNewFile(idx - assistantFiles.length);
        }
    };

    const addFiles = (files: File[]) => {
        // reject duplicates against existing assistantFiles
        const dup = files.filter(f => assistantFiles.some(a => a.name === f.name));
        if (dup.length) return openErrorDialog(`${dup.length} file(s) already exist.`);
        addNewFiles(files);
    };

    const refresh = async () => {
        const token = await getToken(client);
        const uid = (await getUserId(token?.accessToken)).userId;
        const list = await assistantsList(token?.accessToken).catch(() => null);
        if (!list) return appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: null });

        appStateContext?.dispatch({ type: "FETCH_ASSISTANTS", payload: list });
        appStateContext?.dispatch({
            type: "UPDATE_CUSTOM_ASSISTANTS",
            payload: list.filter(a => a.created_by === uid && a.type === "custom")
        });
        appStateContext?.dispatch({
            type: "UPDATE_SHARED_ASSISTANTS",
            payload: list.filter(a => a.created_by !== uid && a.type === "custom")
        });
    };

    // submit
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        setSubmitted(true);
        setBusy(true);
        resetProgress();

        const filteredEx = examples.filter(Boolean);
        const oldFilteredEx = oldExamples.filter(Boolean);

        const nothingChanged =
            title === oldTitle &&
            prompt === oldPrompt &&
            isEqual(filteredEx, oldFilteredEx) &&
            isEqual(contact, oldContact) &&
            isEqual(userEntities, existingUserEntities) &&
            isEqual(adminEntities, existingAdminEntities) &&
            isEqual(fileManagerEntities, existingFileManagerEntities) &&
            uploadedFiles.length === 0 &&
            deletedFiles.length === 0;

        if (nothingChanged) {
            onSubmit();
            setBusy(false);
            console.log("No changes detected, skipping update.");
            return;
        }

        //if assistant is not AskEllie HR or AskEllie GPT, validate title and prompt
        if (id !== "3f4ded35-ad63-4b69-87a5-699d5101762b" && id !== "e4bd5df7-9ec0-484b-bccb-0c24691f4b22") {
            if (!title || !prompt) {
                openErrorDialog("Title and prompt are required.");
                setBusy(false);
                return;
            }
        }

        if (!contact.name || !contact.email) {
            openErrorDialog("Contact name and email are required.");
            setBusy(false);
            return;
        }

        try {
            const token = await getToken(client);

            // 1) Disable assistant
            setCurrentPhase("Disabling assistant...");
            await disableAssistant(id, token?.accessToken);

            // 2) Update metadata
            if (
                title !== oldTitle ||
                prompt !== oldPrompt ||
                !isEqual(contact, oldContact) ||
                !isEqual(userEntities, existingUserEntities) ||
                !isEqual(adminEntities, existingAdminEntities) ||
                !isEqual(fileManagerEntities, existingFileManagerEntities) ||
                !isEqual(filteredEx, oldFilteredEx)
            ) {
                setCurrentPhase("Updating assistant details...");

                console.log("Submitting with user entities:", userEntities);
                console.log("Submitting with admin entities:", adminEntities);
                console.log("Submitting with file manager entities:", fileManagerEntities);

                const userIds = userEntities.filter(e => e.objectType === "user").map(e => e.id);
                const groupIds = userEntities.filter(e => e.objectType === "group").map(e => e.id);
                const adminIds = adminEntities.filter(e => e.objectType === "user").map(e => e.id);
                const adminGroupIds = adminEntities.filter(e => e.objectType === "group").map(e => e.id);
                const fileManagerIds = fileManagerEntities.filter(e => e.objectType === "user").map(e => e.id);
                const fileManagerGroupIds = fileManagerEntities.filter(e => e.objectType === "group").map(e => e.id);

                console.log("Submitting with user ids/groups:", userIds, groupIds);
                console.log("Submitting with admin ids/groups:", adminIds, adminGroupIds);
                console.log("Submitting with file manager ids/groups:", fileManagerIds, fileManagerGroupIds);

                const r = await submitEditAssistant(
                    id,
                    title,
                    prompt,
                    userIds,
                    groupIds,
                    adminIds,
                    adminGroupIds,
                    fileManagerIds,
                    fileManagerGroupIds,
                    filteredEx,
                    contact,
                    token?.accessToken
                );
                if (!r.ok) {
                    openErrorDialog("Failed to update assistant.");
                    setBusy(false);
                    setCurrentPhase("");
                    return;
                }
            }

            // 3) Delete files
            if (deletedFiles.length) {
                setCurrentPhase(`Deleting ${deletedFiles.length} file${deletedFiles.length > 1 ? "s" : ""}...`);
                const r = await deleteAssistantFiles(id, deletedFiles, token?.accessToken);
                if (!r.ok) {
                    openErrorDialog("Failed to delete some files.");
                    setBusy(false);
                    setCurrentPhase("");
                    return;
                }
            }

            // 4) Upload new files one at a time
            if (uploadedFiles.length > 0) {
                const allErrors: Array<{ filename: string; error: string }> = [];

                for (let i = 0; i < uploadedFiles.length; i++) {
                    const file = uploadedFiles[i];
                    setCurrentPhase(`Processing document ${i + 1} of ${uploadedFiles.length}: ${file.name}`);

                    const result = await uploadDocument(id, file, i, uploadedFiles.length, token?.accessToken);

                    if (!result.success) {
                        allErrors.push({
                            filename: file.name,
                            error: result.error || "Unknown error"
                        });
                    }
                }

                if (allErrors.length > 0) {
                    const msg = allErrors.map(e => `${e.filename}: ${e.error}`).join("\n");
                    openErrorDialog(`Some files failed:\n${msg}`);
                    setBusy(false);
                    setCurrentPhase("");
                    return;
                }
            }

            // 5) Re-enable assistant
            setCurrentPhase("Re-enabling assistant...");
            await enableAssistant(id, token?.accessToken);
            await refresh();

            setSuccess(true);
            setCurrentPhase("");
        } catch (e) {
            console.error(e);
            openErrorDialog("An error occurred while updating the assistant.");
            setBusy(false);
            setCurrentPhase("");
        }
    };

    return (
        <div className="relative flex flex-col h-full">
            {/* File Processing Modal - only show when files are being uploaded */}
            <FileProcessingModal
                isVisible={busy && !success && uploadedFiles.length > 0 && currentFileProgress !== null}
                currentPhase={currentPhase}
                currentFileProgress={currentFileProgress}
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
                isEditing={true}
            />

            {/* Original loading spinner (for non-file operations) */}
            {busy && !success && (uploadedFiles.length === 0 || currentFileProgress === null) && (
                <>
                    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm" />

                    {/* Loading message */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="bg-eq-light-grey p-6 rounded-sm shadow-lg pointer-events-auto flex flex-col items-center">
                            <ClipLoader size={40} color="#6D3E91" />
                            <h2 className="text-eq-grey text-md font-semibold mt-4 mb-2 text-center">Your assistant is being updated...</h2>
                            <p className="text-eq-grey text-sm text-center">{currentPhase || "Please wait while we save your changes."}</p>
                        </div>
                    </div>
                </>
            )}

            {/* Success overlay */}
            {success && (
                <>
                    <div className="fixed inset-0 z-50 bg-white" />

                    {/* Success message */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <div className="flex flex-col justify-center items-center pointer-events-auto">
                            <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                                <circle className="checkmark__circle" cx="26" cy="26" r="25" fill="none" />
                                <path className="checkmark__check" fill="none" d="M14 27l7 7 16-16" />
                            </svg>
                            <h2 className="text-xl font-semibold text-eq-green mt-4">Assistant Updated!</h2>
                        </div>
                    </div>
                </>
            )}

            <div className="flex flex-col h-full">
                {/* Header Section */}
                <div className="sticky top-0 z-10 bg-white px-4 pt-6 pb-6 sm:px-6 border-b border-gray-200">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                            <span className="material-symbols-outlined text-[1.5rem] text-eq-green">edit</span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold leading-6 text-gray-900">Edit Assistant</h3>
                            <p className="mt-1 text-sm text-gray-500">Update your assistant's configuration, knowledge base, and sharing settings.</p>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                {/* Conditionally show based on current assistant*/}

                <div className={`flex-grow px-8 py-6 transition-opacity ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                    <div className="mb-6">
                        <TitleField
                            value={title}
                            onChange={setTitle}
                            formSubmitted={submitted}
                            disabled={oldTitle === "AskEllie - HR" || oldTitle === "AskEllie - Secure ChatGPT" ? true : busy}
                            current={oldTitle}
                        />
                    </div>

                    {isAdminMode && (
                        <>
                            <div className="mb-6">
                                <PromptField
                                    value={prompt}
                                    onChange={setPrompt}
                                    formSubmitted={submitted}
                                    disabled={oldTitle === "AskEllie - HR" || oldTitle === "AskEllie - Secure ChatGPT" ? true : busy}
                                    current={oldPrompt}
                                />
                            </div>

                            <div className="mb-6">
                                <h4 className={`text-lg font-semibold ${busy ? "text-gray-500" : "text-eq-grey"}`}>Edit Users</h4>
                                <p className={`text-sm mt-1 mb-4 ${busy ? "text-gray-400" : "text-gray-500"}`}>Share your assistant with other users.</p>
                                <UserSearchBox
                                    onSelectEntity={e => setUserEntities(p => [...p, e])}
                                    selectedEntities={userEntities}
                                    onRemoveEntity={id => {
                                        setUserEntities(p => p.filter(e => e.id !== id));
                                        setAdminEntities(p => p.filter(e => e.id !== id));
                                        setFileManagerEntities(p => p.filter(e => e.id !== id));
                                    }}
                                />
                            </div>

                            <div className="mb-6">
                                <h4 className={`text-lg font-semibold ${busy ? "text-gray-400" : "text-gray-700"}`}>Edit Admins</h4>
                                <p className={`text-sm mt-1 mb-4 ${busy ? "text-gray-400" : "text-gray-500"}`}>Add or remove admin users for this assistant.</p>
                                <UserSearchBox
                                    onSelectEntity={e => {
                                        setAdminEntities(p => [...p, e]);
                                        // also add as user if not already
                                        setUserEntities(p => (p.some(u => u.id === e.id) ? p : [...p, e]));
                                    }}
                                    selectedEntities={adminEntities}
                                    onRemoveEntity={id => setAdminEntities(p => p.filter(e => e.id !== id))}
                                />
                            </div>

                            <div className="mb-6">
                                <h4 className={`text-lg font-semibold ${busy ? "text-gray-400" : "text-gray-700"}`}>Edit File Managers</h4>
                                <p className={`text-sm mt-1 mb-4 ${busy ? "text-gray-400" : "text-gray-500"}`}>
                                    Add or remove file manager users for this assistant.
                                </p>
                                <UserSearchBox
                                    onSelectEntity={e => {
                                        setFileManagerEntities(p => [...p, e]);
                                        // also add as user if not already
                                        setUserEntities(p => (p.some(u => u.id === e.id) ? p : [...p, e]));
                                    }}
                                    selectedEntities={fileManagerEntities}
                                    onRemoveEntity={id => setFileManagerEntities(p => p.filter(e => e.id !== id))}
                                />
                            </div>

                            <div className="mb-6">
                                <ExamplesField examples={examples} onChange={setExamples} disabled={busy} />
                            </div>

                            <div className="mb-6">
                                <ContactsField
                                    contacts={[contact]}
                                    onChange={vals => setContact(vals[0] ?? { name: "", email: "" })}
                                    disabled={busy}
                                    formSubmitted={submitted}
                                />
                            </div>
                        </>
                    )}

                    {isFileManagerMode && (
                        <div className="mb-6">
                            <FileUploadField
                                files={combinedFiles}
                                addFiles={addFiles}
                                removeFile={removeCombined}
                                knowledgeCountLimit={knowledge_limit}
                                knowledgeSizeLimit={knowledge_size}
                                disabled={oldTitle === "AskEllie - Secure ChatGPT" ? true : busy}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className={`sticky bottom-0 border-t border-gray-200 bg-white flex flex-row gap-4 px-4 pb-6 pt-4 sm:flex-row-reverse sm:px-6 ${busy || success ? "z-[60]" : "z-10"}`}
                >
                    {!success && (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={busy}
                            className={`eq-primary-button flex items-center justify-center ${
                                busy ? "opacity-100 cursor-not-allowed" : "bg-eq-green hover:bg-eq-green"
                            }`}
                        >
                            {busy ? <ClipLoader size={16} color="white" /> : "Save Changes"}
                        </button>
                    )}
                    <button type="button" className="eq-secondary-button hover:bg-eq-light-grey" onClick={onCancel}>
                        Close
                    </button>
                </div>
            </div>

            <ErrorDialog open={showErr} setOpen={setShowErr} errorMessage={err} />
        </div>
    );
};
